// eslint-disable-next-line import/named
import { Controller, FieldValues, useFormContext, UseFormRegister, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { JxtBookingOptionsList, JxtInput } from '@jooxter/ui';
import { ResourceTypeDtoMetaTypeEnum } from '@jooxter/api';
import {
  createGTMGAEvent,
  IBookingForm,
  JxtBookingVisibilityEnum,
  JxtEventColorInfos,
  JxtEventColorsEnum,
} from '@jooxter/utils';
import { JxtColorSelect, JxtResourceSelect, JxtUserSelect, JxtVisibilitySelect } from '../../../../components';
import { useFetchUserById } from '../../../../queries';
import { JxtBookingAttendeesField } from '../JxtBookingAttendeesField';
import { JxtBookingRecurrenceField } from '../JxtBookingRecurrenceField';
import { JxtBookingScheduleField } from '../JxtBookingScheduleField';
import { JxtBookingSeatsField } from '../JxtBookingSeatsField';
import { IJxtBookingFormFields } from './types';

/**
 * The form's fields. Which ones exist is decided by the schema the resource produced, so this is a
 * plain read of `schema.shape` - no conditional logic of its own.
 */
export const JxtBookingFormFields = ({
  schema,
  minPhysicalAttendees,
  maxPhysicalAttendees,
  resourceConfiguration,
  resourceState,
  bookingToEdit,
  attendees,
  setAttendees,
}: IJxtBookingFormFields) => {
  const { t } = useTranslation();
  const { register, formState, getFieldState, control } = useFormContext<IBookingForm>();
  const watchOrganizerId = useWatch({ control, name: 'organizerId' });
  const { user: organizer } = useFetchUserById(watchOrganizerId);
  const summaryError = getFieldState('summary').error?.message;

  return (
    <>
      <div className="grid grid-cols-[min-content_minmax(0,1fr)] items-center gap-x-3">
        <i className="fas fa-fw fa-lg fa-quote-left text-neutral-60 col-start-1 row-start-1" />
        <div className="col-start-2 row-start-1">
          <JxtInput
            name="summary"
            placeholder={
              resourceConfiguration?.metaType === ResourceTypeDtoMetaTypeEnum.Vehicle
                ? t<string>('booking-form.summary.vehicle.placeholder')
                : t<string>('booking-form.summary-input-placeholder')
            }
            register={register as unknown as UseFormRegister<FieldValues>}
            formState={formState}
            getFieldState={getFieldState}
            showErrorMessages={false}
          />
        </div>
        {summaryError && typeof summaryError === 'string' && (
          <p className="text-red-100 pt-1 col-start-2 row-start-2">{summaryError}</p>
        )}
      </div>

      <JxtBookingScheduleField
        schema={schema}
        resourceConfiguration={resourceConfiguration}
        bookingToEdit={bookingToEdit}
      />

      {'rrule' in schema.shape && <JxtBookingRecurrenceField />}

      {/* Kept mounted but hidden: the form owns the resource, it is never picked from here. */}
      <Controller
        name="resource"
        control={control}
        render={({ field: { onChange, value } }) => (
          <JxtResourceSelect
            className="hidden"
            onResourceSelect={(val) => onChange(val)}
            value={value ? { value: value.id, label: value.name } : undefined}
            searchOptions={{ bookable: true }}
          />
        )}
      />

      {'physicalAttendees' in schema.shape && (
        <JxtBookingSeatsField
          minPhysicalAttendees={minPhysicalAttendees}
          maxPhysicalAttendees={maxPhysicalAttendees}
          resourceConfiguration={resourceConfiguration}
          resourceState={resourceState}
          bookingToEdit={bookingToEdit}
        />
      )}

      <div className="flex items-center gap-3">
        <i className="fas fa-fw fa-lg fa-user shrink-0 text-neutral-60" />
        <div className="grow min-w-0">
          <Controller
            name="organizerId"
            control={control}
            render={({ field: { onChange } }) => (
              <JxtUserSelect
                placeholder={t<string>('select-owner-placeholder')}
                value={organizer}
                onUserSelect={(value) => {
                  onChange(value.id);
                  createGTMGAEvent('Booking', 'Create Booking', 'Change Organizer');
                }}
                maxMenuHeight={200}
              />
            )}
          />
        </div>
      </div>

      {'internalAttendees' in schema.shape && 'externalAttendees' in schema.shape && (
        <JxtBookingAttendeesField attendees={attendees} setAttendees={setAttendees} />
      )}

      {'options' in schema.shape && resourceConfiguration?.options && resourceConfiguration.options.length > 0 && (
        <div className="grid grid-cols-[min-content_minmax(0,1fr)] items-start gap-3">
          <div className="h-5 flex items-center justify-center">
            <i className="fas fa-cogs fa-lg fa-align-left text-neutral-60" />
          </div>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <div className="text-body-s text-neutral-120 font-medium">{t('booking-form-options')}</div>
              {resourceConfiguration.options.some((option) => option.mandatory) && (
                <div className="text-body-xs text-neutral-80">{t('booking-form-options-required-description')}</div>
              )}
            </div>
            <JxtBookingOptionsList
              options={resourceConfiguration.options}
              register={register}
              getFieldState={getFieldState}
              formState={formState}
              control={control}
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 items-center gap-4">
        <div className="flex items-center gap-3">
          <i className="fas fa-fw fa-lg fa-tint shrink-0 text-neutral-60" />
          <div className="grow">
            <Controller
              name="color"
              control={control}
              render={({ field: { onChange, value } }) => (
                <JxtColorSelect
                  onColorSelect={(color) => {
                    onChange(color);
                    createGTMGAEvent('Booking', 'Create Booking', 'Change Color');
                  }}
                  value={
                    value
                      ? {
                          value: value as JxtEventColorsEnum,
                          label: t<string>(JxtEventColorInfos[value as JxtEventColorsEnum]?.translationKey),
                        }
                      : undefined
                  }
                />
              )}
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <i className="fas fa-fw fa-lg fa-lock shrink-0 text-neutral-60" />
          <div className="grow">
            <Controller
              name="visibility"
              control={control}
              render={({ field: { onChange, value } }) => (
                <JxtVisibilitySelect
                  onVisibilitySelect={(visibility) => {
                    onChange(visibility);
                    createGTMGAEvent('Booking', 'Create Booking', 'Change Visibility');
                  }}
                  value={
                    value
                      ? {
                          value: value as unknown as JxtBookingVisibilityEnum,
                          label: t('visibility-key-select-public-public-private-private', { visibility: value }),
                        }
                      : undefined
                  }
                />
              )}
            />
          </div>
        </div>
      </div>

      <div className="flex items-start gap-3">
        <div className="h-5 my-2 flex items-center justify-center">
          <i className="fas fa-fw fa-lg fa-align-left text-neutral-60" />
        </div>
        <JxtInput
          name="description"
          placeholder={t<string>('booking-form.description-input-placeholder')}
          multiLines={true}
          register={register as unknown as UseFormRegister<FieldValues>}
          formState={formState}
          getFieldState={getFieldState}
        />
      </div>
    </>
  );
};

export default JxtBookingFormFields;
